import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

interface UseReplaceEventFileReturn {
  isLoading: boolean;
  error: Error | null;
  replaceFile: (
    eventId: number,
    file: File,
    scope?: RecurrenceScope,
  ) => Promise<void>;
  reset: () => void;
  isSuccess: boolean;
}

export const useReplaceEventFile = (): UseReplaceEventFileReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      file,
      scope,
    }: {
      eventId: number;
      file: File;
      scope?: RecurrenceScope;
    }) => {
      return eventsApi.replaceEventFile(eventId, file, scope);
    },
    onSuccess: (_, variables) => {
      // イベント関連クエリを無効化してリフレッシュ
      queryInvalidationHelpers.invalidateEventQueries(queryClient);

      // 単一のみ: 対象イベント詳細を無効化
      queryClient.invalidateQueries({
        queryKey: ["eventDetail", variables.eventId],
      });

      // future/all の場合は他のインスタンスも最新化
      if (variables.scope && variables.scope !== "single") {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "eventDetail",
        });
      }
    },
    onError: (error) => {
      console.error("Error replacing event file:", error);
    },
  });

  const replaceFile = useCallback(
    async (
      eventId: number,
      file: File,
      scope?: RecurrenceScope,
    ): Promise<void> => {
      return mutation.mutateAsync({ eventId, file, scope });
    },
    [mutation],
  );

  const reset = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    replaceFile,
    reset,
    isSuccess: mutation.isSuccess,
  };
};
